import React, { useState } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import PhoneInput from 'react-phone-number-input';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReportCase() {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    amount_lost: '',
    description: ''
  });
  const [file, setFile] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('first_name', form.first_name);
      formData.append('last_name', form.last_name);
      formData.append('email', form.email);
      formData.append('phone', form.phone);
      formData.append('amount_lost', parseFloat(form.amount_lost));
      formData.append('description', form.description);
      if (file) {
        formData.append('file', file);
      }

      await axios.post(`${API_URL}/reports/submit`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Case reported successfully! We will contact you soon.');
      setForm({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        amount_lost: '',
        description: ''
      });
      setFile(null);
    } catch (error) {
      toast.error('Failed to submit report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      <section className="pt-32 pb-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight text-white mb-4" data-testid="report-case-heading">
              Report a Case
            </h1>
            <p className="text-lg text-slate-400">
              Submit details about your case and our team will investigate. All information is kept strictly confidential.
            </p>
          </div>

          <Card className="p-8 bg-card border-border">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="first_name">First Name *</Label>
                  <Input
                    id="first_name"
                    value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                    required
                    data-testid="report-firstname-input"
                    className="border-bold"
                  />
                </div>
                <div>
                  <Label htmlFor="last_name">Last Name *</Label>
                  <Input
                    id="last_name"
                    value={form.last_name}
                    onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                    required
                    data-testid="report-lastname-input"
                    className="border-bold"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  data-testid="report-email-input"
                  className="border-bold"
                />
              </div>

              <div>
                <Label htmlFor="phone">Phone Number *</Label>
                <PhoneInput
                  id="phone"
                  international
                  defaultCountry="US"
                  value={form.phone}
                  onChange={(value) => setForm({ ...form, phone: value || '' })}
                  className="PhoneInput"
                  data-testid="report-phone-input"
                />
              </div>

              <div>
                <Label htmlFor="amount_lost">Amount Lost (USD) *</Label>
                <Input
                  id="amount_lost"
                  type="number"
                  step="0.01"
                  value={form.amount_lost}
                  onChange={(e) => setForm({ ...form, amount_lost: e.target.value })}
                  required
                  data-testid="report-amount-input"
                  className="border-bold"
                />
              </div>

              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  rows={6}
                  placeholder="Please provide details about the incident, including transaction IDs, wallet addresses, and any other relevant information..."
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  required
                  data-testid="report-description-input"
                  className="border-bold"
                />
              </div>

              <div>
                <Label htmlFor="file">Upload Documents (Optional)</Label>
                <Input
                  id="file"
                  type="file"
                  onChange={(e) => setFile(e.target.files[0])}
                  data-testid="report-file-input"
                  className="cursor-pointer"
                  className="border-bold"
                />
                <p className="text-xs text-slate-400 mt-2">
                  You can upload screenshots, transaction records, or any other supporting documents.
                </p>
              </div>

              <Button
                type="submit"
                className="w-full bg-[#0066FF] hover:bg-[#0052CC]"
                disabled={loading}
                data-testid="report-submit-button"
              >
                {loading ? 'Submitting...' : 'Submit Report'}
              </Button>
            </form>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
}
